/* global __APP_ENV__ */
import React, { useRef, useState, useEffect } from "react";
import SettingsMenu from "./SettingsMenu";
import { useUXContext } from "./UXContext";
import { updateReaderMode, setReaderMode as cacheReaderMode } from "../utils/webfuse";

// Pick env from __APP_ENV__ (vite) with a safe fallback by hostname
const APP_ENV =
  (typeof __APP_ENV__ !== "undefined" && __APP_ENV__) ||
  (typeof window !== "undefined" &&
    (window.location.hostname === "localhost"
      ? "local"
      : (window.location.hostname.includes("staging") || window.location.hostname.includes("herokuapp"))
        ? "staging"
        : "production"));

const logoSrc =
  APP_ENV === "local"   ? "/img/logoLocal.svg" :
  APP_ENV === "staging" ? "/img/logoStaging.svg" :
                          "/img/logo.svg";

const READER_MODE_KEY = "newsCompare:readerMode";

export default function TopNav({ fetchNewsUsage, onClearCache, onSubmitUrl }) {
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  const { leftPanelOpen, toggleLeftPanel } = useUXContext();

  const [urlOpen, setUrlOpen] = useState(false);
  const [urlText, setUrlText] = useState("");
  const inputRef = useRef(null);

  // Reader Mode UI
  const [readerMode, setReaderMode] = useState(false); // default false if not cached
  const [readerBusy, setReaderBusy] = useState(false);

  // handle spamming
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const onBusy = (e) => {
      const isBusy = !!e.detail?.busy;
      setBusy(isBusy);

      if (isBusy) {
        // collapse left panel and URL input while busy
        if (leftPanelOpen) toggleLeftPanel();
        setUrlOpen(false);
      }
    };
    window.addEventListener("newscompare:busy", onBusy);
    return () => window.removeEventListener("newscompare:busy", onBusy);
  }, [leftPanelOpen, toggleLeftPanel]);

  useEffect(() => {
    if (urlOpen && inputRef.current) inputRef.current.focus();
  }, [urlOpen]);

  // Initialize reader mode from cache; if none, set to true and cache it
  useEffect(() => {
    const raw = localStorage.getItem(READER_MODE_KEY);
    if (raw === null || raw === undefined) {
      setReaderMode(true);
      cacheReaderMode(true);
      console.log("🔵 Reader Mode defaulted to TRUE (cached).");
      // Also notify Webfuse so session state aligns quickly
      updateReaderMode(true).catch((e) => console.warn("Reader mode init update failed:", e));
    } else {
      const v = (() => {
        try { return JSON.parse(raw) === true; } catch { return String(raw).toLowerCase() === "true"; }
      })();
      setReaderMode(v);
      console.log("🔄 Reader Mode loaded from cache:", v);
    }
  }, []);

  // React to global cache clears: reset ALL navbar UI to defaults and sync toggle
  useEffect(() => {
    function onCleared(e) {
      // Close/clear all local UI state
      setUrlOpen(false);
      setUrlText("");
      setShowSettingsMenu(false);
      setReaderBusy(false);
      if (inputRef.current) inputRef.current.blur();
      if (leftPanelOpen) toggleLeftPanel(); // ensure search panel collapses

      // Determine new reader mode after reset
      // Prefer explicit value passed via event.detail.readerMode, else read storage, else default FALSE
      let nextReaderMode;
      if (e?.detail && typeof e.detail.readerMode === "boolean") {
        nextReaderMode = e.detail.readerMode;
      } else {
        const raw = localStorage.getItem(READER_MODE_KEY);
        if (raw == null) {
          nextReaderMode = false; // default after a reset (align with backend reset)
        } else {
          try { nextReaderMode = JSON.parse(raw) === true; }
          catch { nextReaderMode = String(raw).toLowerCase() === "true"; }
        }
      }

      // Update UI toggle and persist locally so UI+storage are consistent post-reset
      setReaderMode(nextReaderMode);
      cacheReaderMode(nextReaderMode);

      console.log("♻️ TopNav reset from cache-cleared event →", {
        urlOpen: false,
        urlText: "",
        showSettingsMenu: false,
        leftPanelOpenAfter: false,
        readerMode: nextReaderMode,
      });
    }

    window.addEventListener("newscompare:cache-cleared", onCleared);
    return () => window.removeEventListener("newscompare:cache-cleared", onCleared);
  }, [leftPanelOpen, toggleLeftPanel]);

  const submitUrl = () => {
    if (busy) return;
    const trimmed = (urlText || "").trim();
    if (!trimmed) return;
    onSubmitUrl?.(trimmed);
    // STEP 1: log the user action
    console.log('FLOW LOG: 1️⃣ TopNav: URL submitted', { url: trimmed, readerMode });

    // dispatch event for new article is being submitted
    window.dispatchEvent(new CustomEvent('newscompare:article-submitted', {
      detail: { source: 'topnav', url: trimmed }
    }));
    // Put UI into busy mode immediately (NavBar/Headlines will collapse/dim via their listeners)
    window.dispatchEvent(new CustomEvent('newscompare:busy', {
      detail: { busy: true, reason: 'article_submit' }
    }));
    // helps SentimentPanel’s “hasNewPostSinceReset” gate
    try { localStorage.setItem('newsCompare:lastPostedArticle', trimmed); } catch { }

    onSubmitUrl?.(trimmed);
  };

  const toggleReaderMode = async () => {
    if (readerBusy) return;
    const next = !readerMode;
    setReaderMode(next);
    setReaderBusy(true);
    cacheReaderMode(next);
    console.log("🧭 Toggling Reader Mode →", next);

    try {
      await updateReaderMode(next);
      console.log("📨 Sent 'update_reader_mode' to Webfuse:", next);
    } catch (e) {
      console.warn("⚠️ updateReaderMode failed:", e);
    } finally {
      // prevent spam for ~1s
      setTimeout(() => setReaderBusy(false), 1000);
    }
  };

  return (
    <nav className="topnav">
      {/* LEFT: logo + search + URL toggle */}
      <div className="topnav-left">
        <div className="topnav-title">
          <img src={logoSrc} alt="ProperGander" className="h-8 w-auto mr-2" />
          PROPER<span className="page-title">GANDER</span>
        </div>

        {/* Search (label slides out to the RIGHT of the icon) */}
        <button
          className={`topnav-button ${leftPanelOpen ? "active" : ""} ${busy ? "is-disabled" : ""}`}
          title={leftPanelOpen ? "Hide search panel" : "Show search panel"}
          onClick={busy ? undefined : toggleLeftPanel}
          disabled={busy}
          aria-disabled={busy}
        >
          <img src="/img/search.svg" alt="Search" className="h-5 w-5" />
          <span className="hover-label hover-label--right">Search</span>
        </button>

        {/* WWW (label slides out to the RIGHT of the icon) */}
        <button
          className={`topnav-button ${urlOpen ? "active" : ""} ${busy ? "is-disabled" : ""}`}
          title={urlOpen ? "Hide URL bar" : "Show URL bar"}
          onClick={() => { if (!busy) setUrlOpen(v => !v); }}
          disabled={busy}
          aria-disabled={busy}
        >
          <img src="/img/www.svg" alt="URL" className="h-5 w-5" />
          <span className="hover-label hover-label--right">Enter URL</span>
        </button>

      </div>

      {/* CENTER: expanding URL bar (left -> right) */}
      <div className={`topnav-center ${urlOpen ? "is-open" : ""} ${busy ? "is-disabled" : ""}`}>
        <div className="url-bar">
          <input
            ref={inputRef}
            type="text"
            placeholder="Paste article URL"
            value={urlText}
            onChange={(e) => setUrlText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") submitUrl();
            }}
            disabled={busy}
          />
          {/* Right-side icon button inside the bar (consistent with nav buttons) */}
          <button
            className="topnav-button url-go-btn"
            title="Open URL"
            onClick={busy ? undefined : submitUrl}
            disabled={busy}
            aria-disabled={busy}>
            <img src="/img/logo.svg" alt="Go" className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* RIGHT: reader mode + settings + refresh */}
      <div className="topnav-right">
        {/* Reader mode group – label, then toggle, then icon */}
        <div className="topnav-section no-divider reader-mode-group">
          <span className="hover-label hover-label--left">READER MODE</span>

          <div className="reader-toggle-popout">
            <button
              type="button"
              role="switch"
              aria-checked={readerMode}
              aria-label="Toggle reader mode"
              className={`reader-toggle-switch ${readerBusy ? "is-busy" : ""}`}
              disabled={readerBusy}
              onClick={toggleReaderMode}
              title={readerMode ? "Reader mode: ON" : "Reader mode: OFF"}
            >
              <span className="reader-toggle-knob" />
            </button>
          </div>

          <button
            className="topnav-button"
            title="Reader mode"
            aria-haspopup="true"
            aria-label="Reader mode settings"
          >
            <img src="/img/book.svg" alt="Reader Mode" className="h-5 w-5" />
          </button>
        </div>

        <div className="topnav-section no-divider">
          <button
            onClick={() => setShowSettingsMenu(!showSettingsMenu)}
            className="topnav-button"
            title="Settings"
          >
            <span className="hover-label hover-label--left">CHECK TOKENS</span>
            <img src="/img/settings.svg" alt="Settings" className="h-5 w-5" />
          </button>
          {showSettingsMenu && (
            <div className="settings-menu-wrapper">
              <SettingsMenu fetchNewsUsage={fetchNewsUsage} />
            </div>
          )}
        </div>

        <div className="topnav-section no-divider">
          <button
            className="topnav-button"
            title="Refresh (clear page data)"
            onClick={() => {
              // let everything become interactive again
              window.dispatchEvent(new CustomEvent('newscompare:busy', {
                detail: { busy: false, reason: 'refresh' }
              }));
              onClearCache?.();
            }}
          >
            <span className="hover-label hover-label--left">RESET</span>
            <img src="/img/refresh.svg" alt="Refresh" className="h-5 w-5" />
          </button>
        </div>
      </div>
    </nav>
  );
}
